import React from "react";
import PropTypes from "prop-types";
import "./CommonDialog.scss";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Close as CloseIcon } from "@mui/icons-material";

export default function CommonDialog({
  cancelButtonProps,
  cancelText,
  children,
  domProps,
  isLoading,
  maxWidth,
  onCancel,
  onClose,
  onSave,
  open,
  paperStyle,
  saveText,
  saveButtonColor,
  showCancelButton,
  showSaveButton,
  title,
  disableSaveButton,
  disableEnforceFocus,
}) {
  const otherProps = { open, ...(domProps || {}) };
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = React.useState(false);

  const handleSave = async () => {
    setLoading(true);
    await onSave?.();
    setLoading(false);
  };

  return (
    <Dialog
      className="common-dialog"
      maxWidth={maxWidth || "sm"}
      fullWidth={true}
      fullScreen={fullScreen}
      disableEnforceFocus={disableEnforceFocus}
      {...otherProps}
      slotProps={{
        paper: {
          sx: { borderRadius: fullScreen ? 0 : "20px", ...paperStyle },
        },
      }}>
      <DialogTitle>
        <div className="common-dialog-header">
          <h2 className="common-dialog-title">{title}</h2>
          <IconButton
            data-testid="t-CommonD-close"
            aria-label="close"
            onClick={onClose}
            size="large"
            sx={{
              position: "absolute",
              right: 16,
              top: 16,
              zIndex: 1,
            }}>
            <CloseIcon fontSize="large" />
          </IconButton>
        </div>
      </DialogTitle>
      <DialogContent>
        <div className="common-dialog-body">{children}</div>
      </DialogContent>
      {(showCancelButton || showSaveButton) && (
        <DialogActions>
          <div className="common-dialog-actions">
            {showCancelButton && (
              <Button
                data-testid="t-CommonD-Button-2"
                color={cancelButtonProps?.color || "secondary"}
                variant={cancelButtonProps?.variant || "outlined"}
                onClick={onCancel || onClose}>
                {cancelText || "CANCEL"}
              </Button>
            )}
            {showSaveButton && (
              <Button
                color={saveButtonColor || "primary"}
                variant="contained"
                onClick={handleSave}
                loading={loading || isLoading}
                disabled={disableSaveButton}>
                {saveText || "SAVE"}
              </Button>
            )}
          </div>
        </DialogActions>
      )}
    </Dialog>
  );
}

CommonDialog.propTypes = {
  cancelButtonProps: PropTypes.object,
  cancelText: PropTypes.string,
  children: PropTypes.node,
  domProps: PropTypes.object,
  isLoading: PropTypes.bool,
  maxWidth: PropTypes.string,
  onCancel: PropTypes.func,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  open: PropTypes.bool,
  paperStyle: PropTypes.object,
  saveText: PropTypes.string,
  saveButtonColor: PropTypes.string,
  showCancelButton: PropTypes.bool,
  showSaveButton: PropTypes.bool,
  title: PropTypes.string,
  disableSaveButton: PropTypes.bool,
  disableEnforceFocus: PropTypes.bool,
};
